
import { useState, useEffect } from 'react';
import { supabase } from '../../utils/supabase';
import type { ProductType as Product } from '../../utils/supabase';
import ContactFooter from '../../components/feature/ContactFooter';

export default function Tienda() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  // Función para verificar si hay sesión válida
  const checkSessionValidity = () => {
    const sessionData = localStorage.getItem('user_session');
    if (!sessionData) return false;

    try {
      const session = JSON.parse(sessionData);
      const now = Date.now();

      if (now > session.expiresAt) {
        localStorage.removeItem('user_session');
        return false;
      }

      return true;
    } catch (error) {
      localStorage.removeItem('user_session');
      return false;
    }
  };

  // Cargar productos
  const loadProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('active', true)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      setProducts(data || []);
    } catch (err) {
      console.error('Error loading products', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setIsLoggedIn(checkSessionValidity());
    loadProducts();
  }, []);

  const handleAdminClick = () => {
    if (isLoggedIn) {
      window.REACT_APP_NAVIGATE('/admin');
    } else {
      window.REACT_APP_NAVIGATE('/login');
    }
  };

  const formatNumber = (num: number) =>
    new Intl.NumberFormat('es-MX').format(num);

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-blue-50">
      {/* Header */}
      <header className="bg-white shadow-lg sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-4">
            <div 
              className="flex items-center space-x-3 cursor-pointer mr-16"
              onClick={() => window.REACT_APP_NAVIGATE('/')}
            >
              <img
                src="/images/image.png"
                alt="Huellitas Unidas Logo"
                className="w-16 h-16 object-contain"
              />
              <div>
                <h1 className="text-2xl font-bold text-gray-800" style={{ fontFamily: '"Pacifico", serif' }}>
                  Huellitas Unidas
                </h1>
                <p className="text-sm text-gray-600">Salvando vidas, una patita a la vez</p>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex gap-2 bg-gray-100 rounded-2xl p-1">
              <button
                onClick={() => {
                  window.REACT_APP_NAVIGATE('/');
                  setTimeout(() => {
                    // Simular click en el tab de donar
                    const event = new CustomEvent('tabChange', { detail: 'donar' });
                    window.dispatchEvent(event);
                  }, 100);
                }}
                className="flex items-center space-x-2 px-4 py-2 rounded-xl font-medium transition-all duration-300 text-gray-600 hover:text-orange-500 hover:bg-white whitespace-nowrap cursor-pointer"
              >
                <i className="ri-heart-fill"></i>
                <span>Donar</span>
              </button>
              <button
                onClick={() => {
                  window.REACT_APP_NAVIGATE('/');
                  setTimeout(() => {
                    // Simular click en el tab de adoptar
                    const event = new CustomEvent('tabChange', { detail: 'adoptar' });
                    window.dispatchEvent(event);
                  }, 100);
                }}
                className="flex items-center space-x-2 px-4 py-2 rounded-xl font-medium transition-all duration-300 text-gray-600 hover:text-blue-500 hover:bg-white whitespace-nowrap cursor-pointer"
              >
                <i className="ri-home-heart-fill"></i>
                <span>Adoptar</span>
              </button>
              <button
                onClick={() => {
                  window.REACT_APP_NAVIGATE('/');
                  setTimeout(() => {
                    // Simular click en el tab de refugios
                    const event = new CustomEvent('tabChange', { detail: 'refugios' });
                    window.dispatchEvent(event);
                  }, 100);
                }}
                className="flex items-center space-x-2 px-4 py-2 rounded-xl font-medium transition-all duration-300 text-gray-600 hover:text-green-500 hover:bg-white whitespace-nowrap cursor-pointer"
              >
                <i className="ri-building-fill"></i>
                <span>Refugios</span>
              </button>
              <button
                onClick={() => window.REACT_APP_NAVIGATE('/mision-huellitas')}
                className="flex items-center space-x-2 px-4 py-2 rounded-xl font-medium transition-all duration-300 text-gray-600 hover:text-purple-500 hover:bg-white whitespace-nowrap cursor-pointer"
              >
                <i className="ri-rocket-fill"></i>
                <span>Misión Huellitas</span>
              </button>
              <button
                className="flex items-center space-x-2 px-4 py-2 rounded-xl font-medium transition-all duration-300 bg-orange-500 text-white shadow-lg whitespace-nowrap cursor-pointer"
              >
                <i className="ri-store-fill"></i>
                <span>Tienda</span>
              </button>
              <button
                onClick={() => window.REACT_APP_NAVIGATE('/register')}
                className="flex items-center space-x-2 px-4 py-2 rounded-xl font-medium transition-all duration-300 text-gray-600 hover:text-purple-500 hover:bg-white whitespace-nowrap cursor-pointer"
              >
                <i className="ri-user-add-fill"></i>
                <span>Registrarse</span>
              </button>
              <button
                onClick={handleAdminClick}
                className="flex items-center space-x-2 px-4 py-2 rounded-xl font-medium transition-all duration-300 text-gray-600 hover:text-gray-800 hover:bg-white whitespace-nowrap cursor-pointer"
              >
                <span>Iniciar sesión</span>
              </button>
            </nav>

            {/* Mobile Menu Button */}
            <div className="lg:hidden">
              <button
                onClick={() => window.REACT_APP_NAVIGATE('/')}
                className="p-2 rounded-lg text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line text-xl"></i>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section
        className="relative text-white py-20 overflow-hidden"
        style={{
          backgroundImage: `url('https://readdy.ai/api/search-image?query=Pet%20store%20with%20cute%20dogs%20and%20products%2C%20online%20pet%20shop%20for%20animal%20welfare%2C%20heartwarming%20pet%20products%20display%2C%20professional%20pet%20retail%20photography%20with%20warm%20lighting&width=1200&height=600&seq=tienda-hero-new&orientation=landscape')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-orange-900/80 to-blue-900/80"></div>
        <div className="relative container mx-auto px-4 text-center">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Tienda Huellitas
            </h1>
            <p className="text-xl md:text-2xl mb-8 opacity-90 leading-relaxed">
              Cada producto que adquieres ayuda directamente a nuestros queridos peluditos
            </p>
            <div className="bg-yellow-500 text-yellow-900 px-6 py-3 rounded-full inline-block font-bold text-lg">
              <i className="ri-heart-fill mr-2"></i>
              El 100% de los fondos recaudados se destina a fortalecer este proyecto y brindar apoyo integral a perritos en situación de abandono.
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Nuestros Productos</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Productos especiales diseñados para ayudar a más perritos necesitados
            </p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <i className="ri-loader-4-line text-4xl text-orange-500 animate-spin mb-4"></i>
              <p className="text-gray-600">Cargando productos...</p>
            </div>
          ) : products.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8" data-product-shop>
              {products.map((product) => {
                const images = [product.image_url, product.image_url_2, product.image_url_3, product.image_url_4].filter(Boolean);
                return (
                  <div key={product.id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                    <div className="relative">
                      <img
                        src={images[0] || 'https://readdy.ai/api/search-image?query=Pet%20product%20merchandise%20for%20animal%20shelter%20fundraising%2C%20cute%20dog%20accessories%20and%20toys%2C%20heartwarming%20pet%20store%20items%20with%20simple%20background&width=400&height=300&seq=product-card&orientation=landscape'}
                        alt={product.name}
                        className="w-full h-64 object-cover"
                      />
                      {images.length > 1 && (
                        <div className="absolute top-3 right-3 bg-black bg-opacity-60 text-white px-2 py-1 rounded-full text-xs">
                          <i className="ri-image-line mr-1"></i>
                          {images.length}
                        </div>
                      )}
                      <div className="absolute top-3 left-3 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                        Disponible
                      </div>
                    </div>
                    
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2">{product.name}</h3>
                      <p className="text-gray-600 text-sm mb-4 line-clamp-3">{product.description}</p>
                      
                      <div className="flex justify-between items-center mb-4">
                        <span className="text-3xl font-bold text-green-600">
                          ${formatNumber(product.price)}
                        </span>
                        <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
                          MXN
                        </span>
                      </div>

                      <button
                        onClick={() => window.REACT_APP_NAVIGATE(`/producto/${product.id}`)}
                        className="w-full bg-gradient-to-r from-orange-500 to-blue-500 text-white py-3 px-4 rounded-xl font-semibold hover:from-orange-600 hover:to-blue-600 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-eye-line mr-2"></i>
                        Ver Detalles
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="bg-orange-100 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-8">
                <i className="ri-box-3-fill text-4xl text-orange-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Próximamente</h3>
              <p className="text-lg text-gray-600 mb-8 max-w-md mx-auto">
                Estamos preparando productos increíbles para ayudar a más perritos necesitados
              </p>
              <button
                onClick={() => window.REACT_APP_NAVIGATE('/')}
                className="bg-orange-500 text-white px-8 py-3 rounded-full font-semibold hover:bg-orange-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-heart-fill mr-2"></i>
                Mientras tanto, dona
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-800 mb-12">
              ¿Por qué comprar en Tienda Huellitas?
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="bg-orange-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-heart-fill text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">Impacto Directo</h3>
                <p className="text-gray-600">
                  El 100% de los fondos recaudados se destina a fortalecer este proyecto y brindar apoyo integral a perritos en situación de abandono
                </p>
              </div>

              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="bg-blue-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-shield-check-fill text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">Calidad Garantizada</h3>
                <p className="text-gray-600">
                  Productos cuidadosamente seleccionados con la mejor calidad para ti y tu mascota
                </p>
              </div>

              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="bg-green-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-community-fill text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">Comunidad Unida</h3>
                <p className="text-gray-600">
                  Únete a una comunidad que trabaja junta para salvar y cuidar a los animales necesitados
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-orange-50 to-blue-50 p-6 rounded-2xl">
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center">
                  <i className="ri-heart-fill text-white text-lg"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-800">Impacto de tu compra</h3>
              </div>
              <p className="text-gray-700">
                El 100% de los fondos recaudados se destina a fortalecer este proyecto y brindar apoyo integral a perritos en situación de abandono.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-gradient-to-r from-orange-600 to-blue-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">¡Cada compra salva vidas!</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto opacity-90">
            No esperes más para hacer la diferencia. Cada producto que compras ayuda directamente a nuestros peluditos
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => window.REACT_APP_NAVIGATE('/')}
              className="bg-white text-orange-600 hover:bg-gray-100 px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              <i className="ri-heart-fill mr-2"></i>
              Hacer Donación
            </button>
            <button
              onClick={() => window.REACT_APP_NAVIGATE('/mision-huellitas')}
              className="bg-white/20 hover:bg-white/30 text-white px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 backdrop-blur-sm whitespace-nowrap cursor-pointer"
            >
              <i className="ri-rocket-fill mr-2"></i>
              Ver Misiones
            </button>
          </div>
        </div>
      </section>

      {/* Contact Footer */}
      <ContactFooter />
    </div>
  );
}
